import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { motion } from "framer-motion";
import { ArrowRight, HelpCircle } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const SECTIONS = [
  {
    group: "Pricing & Plans",
    items: [
      {
        q: "How much does Narrative Vault cost?",
        a: "We have four storage plans: Starter (10 GB) is free, Pro (250 GB) is $29/mo, Studio (1 TB) is $79/mo, and Business (2 TB) is $149/mo. Paid plans are billed monthly and you can upgrade, downgrade, or cancel anytime from your billing portal.",
      },
      {
        q: "What happens on the free Starter plan?",
        a: "Starter is free forever, but to keep storage costs down, vaults created on the Starter plan automatically expire 30 days after they're created. We warn you (in-app and by email) around day 25, and the vault and its clips are permanently deleted on day 30. Upgrade to any paid plan and your vaults never expire.",
      },
      {
        q: "Can I cancel my subscription?",
        a: "Yes — anytime. Open 'Manage plan' in your dashboard to reach the Stripe billing portal, where you can cancel or change plans. Your plan stays active until the end of the billing period.",
      },
    ],
  },
  {
    group: "Selling & Commission",
    items: [
      {
        q: "How much commission does Narrative Vault take on clip sales?",
        a: "A flat 5% per clip sale. Importantly, that 5% is structured to cover payment-processing costs, so you're never surprised by hidden fees — the number you see is the number that applies. The rest goes to you.",
      },
      {
        q: "So what do I actually keep on a sale?",
        a: "You keep your clip price minus the 5% platform fee (which absorbs standard card-processing costs). For example, on a $20 clip you receive approximately $19 after our 5% — with processing already accounted for on our side, not stacked on top.",
      },
      {
        q: "How do I get paid?",
        a: "You connect your bank via Stripe from the Payouts page, and your earnings are paid directly to your bank account. On each clip sale we automatically split the payment — you receive 95% and Narrative Vault keeps a 5% platform fee (which covers processing).",
      },
      {
        q: "How do I set clip prices?",
        a: "You set the price of every individual clip when you upload it, and can edit prices anytime before or after publishing. You also choose how many complimentary downloads clients get per event (default 5).",
      },
    ],
  },
  {
    group: "For Your Clients",
    items: [
      {
        q: "Do my clients need an account?",
        a: "No. Clients open your private vault link and can stream every clip immediately. They only enter an email when they want to unlock, purchase, or download — so we can track their access.",
      },
      {
        q: "How do complimentary downloads work?",
        a: "Each event includes a number of free downloads you choose (default 5). Clients can spend them on ANY clips they like — not just the first few. Once those are used up, remaining clips can be purchased individually or all at once.",
      },
      {
        q: "What do clients get after purchasing?",
        a: "Permanent access to download the original, high-quality file. Purchased and unlocked clips stay available to them, and they can grab everything at once with 'Download all'.",
      },
    ],
  },
  {
    group: "Storage & Data",
    items: [
      {
        q: "What counts toward my storage limit?",
        a: "The video files you upload across all your vaults. If you go over your plan's limit, uploads still work — we'll just nudge you to upgrade so everything stays smooth.",
      },
      {
        q: "Are my vaults private?",
        a: "Yes. Vaults are only accessible via the unique private link you generate when you publish. Nothing is listed publicly or searchable.",
      },
    ],
  },
];

export default function FAQ() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70">
            <HelpCircle className="h-3.5 w-3.5 text-[#00F0FF]" /> Transparency first
          </span>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter">
            Frequently asked <span className="text-[#00F0FF]">questions</span>
          </h1>
          <p className="mt-5 text-lg text-white/60 max-w-2xl">
            Clear pricing, a fair 5% commission that covers processing, and no surprises.
            Here's exactly how Narrative Vault works.
          </p>
        </motion.div>

        <div className="mt-14 space-y-12">
          {SECTIONS.map((section, i) => (
            <motion.section
              key={section.group}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`faq-section-${i}`}
            >
              <h2 className="font-display text-sm uppercase tracking-[0.25em] text-[#00F0FF] mb-4">
                {section.group}
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {section.items.map((item, j) => (
                  <AccordionItem
                    key={item.q}
                    value={`${i}-${j}`}
                    data-testid={`faq-item-${i}-${j}`}
                    className="border-white/10"
                  >
                    <AccordionTrigger className="text-left text-base font-medium hover:text-[#00F0FF] hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-white/60 text-sm leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.section>
          ))}
        </div>

        <div className="mt-16 rounded-3xl border border-white/10 bg-gradient-to-br from-[#0d0d0d] to-black p-10 text-center">
          <h3 className="font-display text-2xl font-bold">Still have questions?</h3>
          <p className="text-white/50 mt-2">Start free — no card needed for the Starter plan.</p>
          <Link
            to="/register"
            data-testid="faq-cta"
            className="mt-6 inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-7 py-3.5 hover:scale-105 transition-transform duration-300 cyan-glow"
          >
            Create your first vault <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/40">
          <Link to="/" className="font-display font-semibold text-white">Narrative<span className="text-[#00F0FF]">Vault</span></Link>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/copyright" className="hover:text-white transition-colors">Copyright</Link>
            <span>© {new Date().getFullYear()} — by WTN Productions</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
